import React, { useEffect, useState } from "react";
import { ArrowLeftIcon, ArrowRightIcon } from "@chakra-ui/icons";
import {
  VStack,
  Button,
  Box,
  HStack,
  Text,
  Center,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import StepHeader from "../../../../../../components/StepperHeader";
import CauseForm from "../../../../../../components/diagrams/fishbone/fishboneDiagramInput";
import FishboneDiagram from "../../../../../../components/diagrams/fishbone/fishbone";
import { Cause } from "../../../../../../utils/types/cause";
import { FishboneNode } from "../../../../../../components/diagrams/fishbone/Fishbone.types";
import { FaCompress, FaExpand, FaExclamationTriangle } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../../redux/reducer/rootReducer";
import {
  getFormData,
  saveFormData,
} from "../../../../../../redux/slice/formSlice";
import { rootCauseAnalysis } from "../forms";
import { useFormik } from "formik";
import { set } from "lodash";

interface RootCauseAnalysisProps {
  onNext: () => void;
  onPrev: () => void;
  onSave: () => void;
}

const RootCauseAnalysis = (props: RootCauseAnalysisProps) => {
  const [causes, setCauses] = useState<any[]>([]);
  const [rawCauses, setRawCauses] = useState<any[]>([]);

  const [expandFishbone, setExpandFishbone] = useState(false);
  const [refreshFishbone, setRefreshFishbone] = useState(false);
  const [isGeneratingDigaram, setIsGeneratingDigaram] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessages, setErrorMessages] = useState("");
  const [diagramKey, setDiagramKey] = useState(0);
  const projectInformationData = useSelector((state: RootState) =>
    getFormData(state, "2")
  );
  const stepData = useSelector((state: RootState) => getFormData(state, "4"));
  const [effect, setEffect] = useState(projectInformationData?.problem || "");
  const [saveAndExist, setSaveAndExist] = useState(false);

  const dispatch = useDispatch();
  const toast = useToast();

  const formik = useFormik({
    ...rootCauseAnalysis,
    initialValues: stepData,
    onSubmit: (values) => {
      console.log("Form submit values: ", values);

      if (values.causes.length === 0) {
        setShowErrorMessage(true);
        setErrorMessages("Please add atleast one cause");
        return;
      } else {
        setShowErrorMessage(false);
        dispatch(saveFormData({ step: "4", data: values }));
        if (!saveAndExist) {
          props.onNext();
        } else {
          setTimeout(() => {
            if (props.onSave) props.onSave();
          }, 2000);
        }
      }
    },
  });

  const transformFormValuesForFishBone = (causes: Cause[]) => {
    const nodeDataArray: FishboneNode[] = [];

    Array.isArray(causes) &&
      causes.forEach((cause, index) => {
        if (cause.key.trim() !== "") {
          nodeDataArray.push({
            name: cause.category,
            children: cause.primaryCauses.map((subCause) => ({
              name: subCause.primary,
              children: subCause.secondaryCauses.map((secCause) => ({
                name: secCause,
                brokenLine: true,
              })),
            })),
          });
        }
      });

    return nodeDataArray;
  };

  const handleFormSubmit = async (effect: string, causes: any[]) => {
    setIsGeneratingDigaram(true);
    setTimeout(() => {
      formik.setFieldValue("effect", effect);
      formik.setFieldValue("causes", causes);
      setCauses(causes);
      setCauses(transformFormValuesForFishBone(causes));
      setIsGeneratingDigaram(false);
    }, 3000);
  };

  const handleSaveLocally = async (effect: string, causes: any[]) => {
    console.log("Causes: ", causes);

    setRawCauses(causes);
    formik.setFieldValue("effect", effect);
    formik.setFieldValue("causes", causes);
    console.log("Formik values: ", formik.values);

    setCauses(transformFormValuesForFishBone(causes));

    setEffect(effect);
    setDiagramKey(diagramKey + 1);
  };
  return (
    <VStack h="100%" w="100%" spacing={4}>
      {showErrorMessage &&
        toast({
          position: "top-right",
          render: () => (
            <Box color="white" p={3} bg="red" borderRadius={4}>
              {errorMessages}
            </Box>
          ),
        })}
      {/* Step Header */}
      <StepHeader
        title="Step 3: Root Cause Analysis"
        description="Now that we have stated our Problem and Aim statements, we will try to identify root causes for our problem. We will be using fish bone diagram to do this. It will help us visualize our problem with its possible causes.
For other problem identification tools"
      />

      {/* Form Section */}
      <Box
        w="full"
        h={"full"}
        display={"flex"}
        flexDirection={"row"}
        p={4}
        flex={1}
      >
        {/* <Box w={"30%"}>
           <CauseForm
             onSubmit={handleFormSubmit}
             onSaveLocally={handleSaveLocally}
             previousCauses={rawCauses}
             previousEffect={effect}
           />
         </Box> */}

        <Box
          w={"100%"}
          h={"100%"}
          pt={4}
          pb={4}
          display={"flex"}
          flexDirection={"row"}
          gap={10}
        >
          <Box w={"35%"} display={expandFishbone ? "none" : "block"}>
            <CauseForm
              onSubmit={handleFormSubmit}
              onSaveLocally={handleSaveLocally}
              previousCauses={rawCauses}
              previousEffect={effect}
              formValues={stepData as any}
            />
          </Box>

          <Box
            w={expandFishbone ? "full" : "65%"}
            borderColor={"#f4f4f4"}
            borderWidth={2}
            position={"sticky"}
          >
            <HStack
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"flex-end"}
              bg={"gray.200"}
              p={4}
            >
              {expandFishbone ? (
                <FaCompress
                  cursor={"pointer"}
                  onClick={() => {
                    setExpandFishbone(!expandFishbone);
                    setRefreshFishbone(true);
                    setTimeout(() => {
                      setRefreshFishbone(false);
                    }, 1500);
                  }}
                />
              ) : (
                <FaExpand
                  cursor={"pointer"}
                  onClick={() => {
                    setExpandFishbone(!expandFishbone);
                    setRefreshFishbone(true);
                    setTimeout(() => {
                      setRefreshFishbone(false);
                    }, 1500);
                  }}
                />
              )}
            </HStack>
            {effect.length === 0 ? (
              <Center
                h={"100%"}
                display={"flex"}
                flexDirection={"column"}
                alignContent={"center"}
                justifyContent={"center"}
                gap={30}
              >
                <FaExclamationTriangle fontSize={32} color="red" />
                <Text>
                  Please enter valid data to see your fishbone diagram
                </Text>
              </Center>
            ) : (
              <>
                {!refreshFishbone ? (
                  <FishboneDiagram
                    key={diagramKey}
                    props={causes}
                    effect={effect}
                  />
                ) : (
                  <Center h={"100%"}>
                    <Spinner
                      thickness="4px"
                      speed="0.65s"
                      emptyColor="gray.200"
                      color="blue.500"
                      size="sm"
                    />
                  </Center>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
      <HStack
        flex={0.1}
        w="100%"
        justifyContent="space-between"
        p={4}
        bg="gray.50"
      >
        <Button
          colorScheme="blue"
          variant="outline"
          leftIcon={<ArrowLeftIcon />}
          onClick={() => {
            //  if (showSummarizedAimStatement) {
            //    formik.setFieldValue("aimStatement", "");
            //    setShowSummarizedAimStatement(false);
            //  } else {
            if (props.onPrev) {
              props.onPrev();
            }
          }}
        >
          Previous
        </Button>
        <HStack>
          <Button
            colorScheme="blue"
            isDisabled={!formik.isValid}
            variant="outline"
            onClick={() => {
              // if (props.onSave) props.onSave();
              // console.log("Saved values:", formik.values);
              setSaveAndExist(true);
              formik.handleSubmit();
            }}
          >
            Save & Exit
          </Button>
          <Button
            colorScheme="blue"
            isDisabled={!formik.isValid}
            onClick={() => {
              console.log("Form errors: ", formik.errors);
              console.log("Form values: ", formik.values);
              setSaveAndExist(false);
              // if (formik.errors && Object.keys(formik.errors).length > 0) {
              //   //  setShowErrorMessage(true);
              //   //  setErrorMessages(formik.errors.toString());
              //    return;
              // }
              formik.handleSubmit();
            }}
            w={"50%"}
            rightIcon={<ArrowRightIcon />}
          >
            Next
          </Button>
        </HStack>
      </HStack>
    </VStack>
  );
};

export default RootCauseAnalysis;
